/**
 * Partner Name for a Vertiv viewer: one field. Typed text is the value.
 * Suggestions open from the field and stay inside the viewport, above the
 * corner action. "Other" does not store the word Other and does not add a
 * second input — it clears a chosen suggestion so the same field can be typed.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  ScrollView,
  Platform,
  StyleSheet,
  Dimensions,
  BackHandler,
  type TextInput,
} from 'react-native';

import { FormInput, formFieldInStack } from '../../../../components/forms';
import { ChevronDown, ChevronUp } from '../../../../components/icons';
import { colors } from '../../../../theme/colors';
import { elevation } from '../../../../theme/elevation';
import { spacing } from '../../../../theme/spacing';
import { fontSize, lineHeight } from '../../../../theme/typography';
import {
  acquireWebOverlayScrollLock,
  releaseWebOverlayScrollLock,
} from '../../../../utils/webOverlayScrollLock';

import type { CSSProperties, ReactNode } from 'react';

const PARTNER_NAME_OTHER = 'Other';
/** Room left under the menu so the corner action does not cover the last row. */
const MENU_BOTTOM_CLEARANCE = 72;

let createPortal: ((ui: ReactNode, container: Element) => ReactNode) | null =
  null;
if (Platform.OS === 'web') {
  try {
    // @ts-expect-error -- optional peer; tests may omit react-dom
    createPortal = require('react-dom').createPortal as (
      ui: ReactNode,
      container: Element,
    ) => ReactNode;
  } catch {
    createPortal = null;
  }
}

export type AnchoredMenuLayout = {
  left: number;
  width: number;
  maxHeight: number;
  top?: number;
  bottom?: number;
};

export function layoutAnchoredMenu(args: {
  triggerLeft: number;
  triggerTop: number;
  triggerBottom: number;
  triggerWidth: number;
  viewportWidth: number;
  viewportHeight: number;
}): AnchoredMenuLayout {
  const gap = spacing.formDropdownMenuOffsetY;
  const edge = spacing.formDropdownViewportMargin;
  const cap = spacing.formDropdownMenuMaxHeight;
  const spaceBelow =
    args.viewportHeight - args.triggerBottom - gap - MENU_BOTTOM_CLEARANCE;
  const spaceAbove = args.triggerTop - gap - edge;
  const width = Math.max(
    0,
    Math.min(args.triggerWidth, args.viewportWidth - edge * 2),
  );
  let left = args.triggerLeft;
  if (left + width > args.viewportWidth - edge) {
    left = Math.max(edge, args.viewportWidth - edge - width);
  }
  if (left < edge) {
    left = edge;
  }
  const openBelow = spaceBelow >= 120 || spaceBelow >= spaceAbove;
  if (openBelow) {
    return {
      left,
      width,
      top: args.triggerBottom + gap,
      maxHeight: Math.min(cap, Math.max(0, spaceBelow)),
    };
  }
  return {
    left,
    width,
    bottom: args.viewportHeight - args.triggerTop + gap,
    maxHeight: Math.min(cap, Math.max(0, spaceAbove)),
  };
}

function visiblePartnerOptions(
  suggestions: readonly string[],
  query: string,
): string[] {
  const names = suggestions.filter(
    name => name.trim().length > 0 && name !== PARTNER_NAME_OTHER,
  );
  const q = query.trim().toLowerCase();
  const matched =
    q.length === 0
      ? names
      : names.filter(name => name.toLowerCase().includes(q));
  const showOther =
    q.length === 0 || PARTNER_NAME_OTHER.toLowerCase().includes(q);
  return showOther ? [...matched, PARTNER_NAME_OTHER] : matched;
}

function PartnerNameOptions({
  options,
  value,
  onSelect,
  onOptionPressIn,
}: {
  options: string[];
  value: string;
  onSelect: (next: string) => void;
  onOptionPressIn?: () => void;
}) {
  if (options.length === 0) {
    return (
      <Text style={styles.empty} accessibilityRole="text">
        No matching partners
      </Text>
    );
  }
  return (
    <>
      {options.map(option => {
        const selected = option !== PARTNER_NAME_OTHER && option === value;
        return (
          <Pressable
            key={option}
            testID={`dropdown-option-${option}`}
            accessibilityRole="button"
            accessibilityLabel={
              option === PARTNER_NAME_OTHER
                ? 'Enter a partner name'
                : `Select ${option}`
            }
            onPressIn={onOptionPressIn}
            onPress={() => onSelect(option)}
            style={[
              styles.option,
              option === PARTNER_NAME_OTHER && styles.otherOption,
              selected && styles.optionSelected,
            ]}
          >
            <Text style={styles.optionText}>{option}</Text>
          </Pressable>
        );
      })}
    </>
  );
}

export function PartnerNameField({
  value,
  suggestions,
  onChange,
  error,
}: {
  value: string;
  suggestions: readonly string[];
  onChange: (next: string) => void;
  error?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [menuBox, setMenuBox] = useState<AnchoredMenuLayout | null>(null);
  const anchorRef = useRef<View>(null);
  const inputRef = useRef<TextInput>(null);
  const suppressOpenRef = useRef(false);
  const ignoreBlurRef = useRef(false);
  const unmountedRef = useRef(false);
  const blurCloseTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const ignoreResetTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suppressOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const options = useMemo(
    () => visiblePartnerOptions(suggestions, query),
    [suggestions, query],
  );

  useEffect(() => {
    return () => {
      unmountedRef.current = true;
      if (blurCloseTimer.current) {
        clearTimeout(blurCloseTimer.current);
      }
      if (ignoreResetTimer.current) {
        clearTimeout(ignoreResetTimer.current);
      }
      if (suppressOpenTimer.current) {
        clearTimeout(suppressOpenTimer.current);
      }
    };
  }, []);

  const clearBlurCloseTimer = () => {
    if (blurCloseTimer.current) {
      clearTimeout(blurCloseTimer.current);
      blurCloseTimer.current = null;
    }
  };

  const cancelBlurClose = () => {
    ignoreBlurRef.current = true;
    clearBlurCloseTimer();
    if (ignoreResetTimer.current) {
      clearTimeout(ignoreResetTimer.current);
    }
    ignoreResetTimer.current = setTimeout(() => {
      ignoreResetTimer.current = null;
      ignoreBlurRef.current = false;
    }, 400);
  };

  const armBlurClose = () => {
    if (unmountedRef.current) {
      return;
    }
    clearBlurCloseTimer();
    blurCloseTimer.current = setTimeout(() => {
      blurCloseTimer.current = null;
      if (unmountedRef.current) {
        return;
      }
      if (ignoreBlurRef.current) {
        armBlurClose();
        return;
      }
      setOpen(false);
    }, 200);
  };

  const measureMenu = () => {
    const node = anchorRef.current as unknown as {
      getBoundingClientRect?: () => {
        left: number;
        top: number;
        bottom: number;
        width: number;
      };
      measureInWindow?: (
        callback: (x: number, y: number, width: number, height: number) => void,
      ) => void;
    } | null;
    if (node == null) {
      return;
    }
    if (
      Platform.OS === 'web' &&
      node.getBoundingClientRect &&
      typeof window !== 'undefined'
    ) {
      const rect = node.getBoundingClientRect();
      setMenuBox(
        layoutAnchoredMenu({
          triggerLeft: rect.left,
          triggerTop: rect.top,
          triggerBottom: rect.bottom,
          triggerWidth: rect.width,
          viewportWidth: window.innerWidth,
          viewportHeight: window.innerHeight,
        }),
      );
      return;
    }
    node.measureInWindow?.((x, y, width, height) => {
      const screen = Dimensions.get('window');
      setMenuBox(
        layoutAnchoredMenu({
          triggerLeft: x,
          triggerTop: y,
          triggerBottom: y + height,
          triggerWidth: width,
          viewportWidth: screen.width,
          viewportHeight: screen.height,
        }),
      );
    });
  };

  useEffect(() => {
    if (Platform.OS !== 'android' || !open) {
      return undefined;
    }
    const subscription = BackHandler.addEventListener(
      'hardwareBackPress',
      () => {
        setOpen(false);
        return true;
      },
    );
    return () => subscription.remove();
  }, [open]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }
    measureMenu();
    if (Platform.OS !== 'web' || typeof window === 'undefined') {
      return undefined;
    }
    const update = () => measureMenu();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !open) {
      return undefined;
    }
    acquireWebOverlayScrollLock();
    return () => {
      releaseWebOverlayScrollLock();
    };
  }, [open]);

  useEffect(() => {
    if (Platform.OS !== 'web' || !open || typeof document === 'undefined') {
      return undefined;
    }
    const onDown = (event: Event) => {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      const portal = document.querySelector('[data-partner-name-menu="true"]');
      const anchor = anchorRef.current as unknown as HTMLElement | null;
      if (anchor?.contains(target) || portal?.contains(target)) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => {
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const suppressOpenUntilFocus = () => {
    suppressOpenRef.current = true;
    if (suppressOpenTimer.current) {
      clearTimeout(suppressOpenTimer.current);
      suppressOpenTimer.current = null;
    }
  };

  const suppressOpenBriefly = () => {
    suppressOpenRef.current = true;
    if (suppressOpenTimer.current) {
      clearTimeout(suppressOpenTimer.current);
    }
    suppressOpenTimer.current = setTimeout(() => {
      suppressOpenTimer.current = null;
      suppressOpenRef.current = false;
    }, 50);
  };

  const choose = (next: string) => {
    cancelBlurClose();
    if (next === PARTNER_NAME_OTHER) {
      setQuery('');
      setOpen(false);
      if (suggestions.includes(value)) {
        onChange('');
      }
      const input = inputRef.current;
      const alreadyFocused =
        input != null &&
        typeof input.isFocused === 'function' &&
        input.isFocused();
      if (input != null && !alreadyFocused) {
        suppressOpenUntilFocus();
        input.focus();
      }
      return;
    }
    setQuery('');
    setOpen(false);
    onChange(next);
  };

  const menu =
    open && menuBox != null ? (
      <PartnerNameMenu box={menuBox}>
        <PartnerNameOptions
          options={options}
          value={value}
          onSelect={choose}
          onOptionPressIn={cancelBlurClose}
        />
      </PartnerNameMenu>
    ) : null;

  return (
    <View testID="field-partner-name" style={formFieldInStack}>
      <View ref={anchorRef} collapsable={false}>
        <FormInput
          ref={inputRef}
          label="Channel Partner"
          value={value}
          onChangeText={next => {
            onChange(next);
            setQuery(next);
            const q = next.trim().toLowerCase();
            const hasMatch =
              q.length === 0 ||
              suggestions.some(name => name.toLowerCase().includes(q));
            setOpen(hasMatch);
          }}
          onFocus={() => {
            clearBlurCloseTimer();
            if (suppressOpenRef.current) {
              suppressOpenRef.current = false;
              return;
            }
            setQuery('');
            setOpen(true);
          }}
          placeholder="Enter partner/reseller name"
          error={error}
          errorElementId="partner-name-error"
          inputStyle={styles.inputWithChevron}
          onBlur={Platform.OS === 'web' ? undefined : armBlurClose}
        />
        <Pressable
          testID="dropdown-trigger"
          accessibilityRole="button"
          accessibilityLabel="Show partner names"
          accessibilityState={{ expanded: open }}
          onPressIn={cancelBlurClose}
          onPress={() => {
            if (open) {
              suppressOpenBriefly();
              setOpen(false);
              return;
            }
            setQuery('');
            setOpen(true);
            inputRef.current?.focus();
          }}
          style={styles.chevron}
        >
          {open ? (
            <ChevronUp
              size={spacing.formDropdownChevronSize}
              color={colors.textSecondary}
            />
          ) : (
            <ChevronDown
              size={spacing.formDropdownChevronSize}
              color={colors.textSecondary}
            />
          )}
        </Pressable>
      </View>
      {Platform.OS === 'web' ? menu : null}
      {Platform.OS !== 'web' && open ? (
        <View
          testID="partner-name-suggestions"
          style={[styles.menu, styles.nativeMenu]}
        >
          <ScrollView
            style={{ maxHeight: spacing.formDropdownMenuMaxHeight }}
            keyboardShouldPersistTaps="always"
            keyboardDismissMode="none"
            nestedScrollEnabled
          >
            <PartnerNameOptions
              options={options}
              value={value}
              onSelect={choose}
              onOptionPressIn={cancelBlurClose}
            />
          </ScrollView>
        </View>
      ) : null}
    </View>
  );
}

function PartnerNameMenu({
  box,
  children,
}: {
  box: AnchoredMenuLayout;
  children: ReactNode;
}) {
  if (
    Platform.OS !== 'web' ||
    createPortal == null ||
    typeof document === 'undefined'
  ) {
    return null;
  }
  const style: CSSProperties = {
    position: 'fixed',
    left: box.left,
    width: box.width,
    maxHeight: box.maxHeight,
    zIndex: 999999,
    overflowY: 'auto',
    overflowX: 'hidden',
    backgroundColor: colors.backgroundPrimary,
    border: `1px solid ${colors.borderCard}`,
    borderRadius: spacing.formDropdownBorderRadius,
    boxSizing: 'border-box',
    ...(box.top != null ? { top: box.top } : {}),
    ...(box.bottom != null ? { bottom: box.bottom } : {}),
    ...(elevation.dropdownPanel as CSSProperties),
  };
  return createPortal(
    <div
      data-dropdown-portal="true"
      data-partner-name-menu="true"
      style={style}
    >
      {children}
    </div>,
    document.body,
  );
}

const styles = StyleSheet.create({
  inputWithChevron: {
    paddingRight: spacing.formControlPaddingHorizontal + 24,
  },
  chevron: {
    position: 'absolute',
    top: lineHeight.formLabel + spacing.fieldVerticalGap,
    right: spacing.xs,
    height: spacing.formControlHeight,
    width: 28,
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2,
  },
  menu: {
    backgroundColor: colors.backgroundPrimary,
    borderWidth: 1,
    borderColor: colors.borderCard,
    borderRadius: spacing.formDropdownBorderRadius,
    overflow: 'hidden',
  },
  nativeMenu: {
    marginTop: spacing.formDropdownMenuOffsetY,
    width: '100%',
  },
  option: {
    minHeight: spacing.formDropdownOptionMinHeight,
    paddingHorizontal: spacing.formDropdownOptionPaddingHorizontal,
    justifyContent: 'center',
  },
  otherOption: {
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.borderCard,
  },
  optionSelected: {
    backgroundColor: colors.surfaceSelectSelected,
  },
  optionText: {
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textPrimary,
  },
  empty: {
    padding: spacing.md,
    fontSize: fontSize.formControl,
    lineHeight: lineHeight.formControl,
    color: colors.textSecondary,
  },
});
